import { useEffect, useState } from 'react'
import AdminLayout from '../../components/shell/AdminLayout'
import EmptyState from '../../components/shell/EmptyState'
import LeaveTypeForm from '../../components/admin/LeaveTypeForm'
import RetireCatalogEntryForm from '../../components/admin/RetireCatalogEntryForm'
import { createLeaveType, getAdminLeaveTypes, updateLeaveType, type AdminLeaveType } from './catalogStore'

// The one form open at a time: creating a leave type, or acting on one row.
type Panel = { kind: 'create' } | { kind: 'edit' | 'retire'; id: string }

const ACTION =
  'flex min-h-11 items-center justify-center rounded-control border border-line-strong px-3 text-small font-bold hover:bg-surface-2'

/**
 * docs/product/scheduling-model.md's leave types: a name and an active flag,
 * which a leave request names. Every leave type with how many requests use it,
 * and forms to create one, rename it, and retire or restore it. A retired type
 * is kept, so the requests that name it still read it, and drops out of the
 * request form. The last open type stays open, since a request needs one to
 * choose.
 */
export default function AdminLeaveTypesPage() {
  const [types, setTypes] = useState<AdminLeaveType[]>([])
  const [loaded, setLoaded] = useState(false)
  const [panel, setPanel] = useState<Panel | null>(null)
  const [saving, setSaving] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    getAdminLeaveTypes().then((loadedTypes) => {
      if (cancelled) return
      setTypes(loadedTypes)
      setLoaded(true)
    })
    return () => {
      cancelled = true
    }
  }, [])

  const panelType = panel && panel.kind !== 'create' ? (types.find((type) => type.id === panel.id) ?? null) : null
  const openCount = types.filter((type) => type.active).length

  function openPanel(next: Panel) {
    setNotice(null)
    setFormError(null)
    setPanel(next)
  }

  function closePanel() {
    setPanel(null)
    setFormError(null)
  }

  async function run(action: () => Promise<{ ok: true } | { ok: false; message: string }>, done: string) {
    setSaving(true)
    setFormError(null)
    const result = await action()
    setSaving(false)
    if (!result.ok) {
      setFormError(result.message)
      return
    }
    setTypes(await getAdminLeaveTypes())
    setPanel(null)
    setNotice(done)
  }

  const create = (name: string) => run(() => createLeaveType(name), `Created ${name}.`)
  const rename = (name: string) => (panelType ? run(() => updateLeaveType(panelType.id, name, panelType.active), `Renamed to ${name}.`) : Promise.resolve())
  const retire = () => (panelType ? run(() => updateLeaveType(panelType.id, panelType.name, false), `Retired ${panelType.name}.`) : Promise.resolve())
  const restore = (type: AdminLeaveType) => {
    setNotice(null)
    return run(() => updateLeaveType(type.id, type.name, true), `Restored ${type.name}.`)
  }

  return (
    <AdminLayout current="leave-types" title="Leave types" subtitle="The types of leave an agent can request">
      <div className="flex flex-wrap items-center gap-3 rounded-surface border border-line bg-canvas p-3">
        <button
          type="button"
          aria-expanded={panel?.kind === 'create'}
          onClick={() => openPanel({ kind: 'create' })}
          className="flex min-h-11 items-center justify-center rounded-control bg-primary px-4 text-small font-bold text-primary-ink hover:bg-primary-hover"
        >
          Create leave type
        </button>
      </div>

      {notice && (
        <p role="status" className="text-small font-semibold text-success">
          {notice}
        </p>
      )}

      {formError && !panel && (
        <p role="alert" className="text-small font-semibold text-error">
          {formError}
        </p>
      )}

      {panel?.kind === 'create' && <LeaveTypeForm key="create" saving={saving} error={formError} onSave={create} onCancel={closePanel} />}
      {panel?.kind === 'edit' && panelType && (
        <LeaveTypeForm key={panelType.id} leaveType={panelType} saving={saving} error={formError} onSave={rename} onCancel={closePanel} />
      )}
      {panel?.kind === 'retire' && panelType && (
        <RetireCatalogEntryForm
          key={panelType.id}
          name={panelType.name}
          message={`Retiring ${panelType.name} takes it out of the request form. ${
            panelType.requests === 0
              ? 'No leave request names it.'
              : `The ${panelType.requests} ${panelType.requests === 1 ? 'request that names' : 'requests that name'} it keep reading it.`
          }`}
          blocked={openCount <= 1 ? `${panelType.name} is the only open leave type. A leave request needs one to choose, so create another first.` : undefined}
          confirmLabel="Retire leave type"
          saving={saving}
          error={formError}
          onConfirm={retire}
          onCancel={closePanel}
        />
      )}

      {!loaded ? (
        <p role="status" className="text-small text-ink-muted">
          Loading&hellip;
        </p>
      ) : types.length === 0 ? (
        <EmptyState>No leave types have been created yet.</EmptyState>
      ) : (
        <div className="overflow-x-auto rounded-surface border border-line">
          <table aria-label="Leave types" className="w-full border-collapse">
            <thead>
              <tr>
                {['Leave type', 'Requests', 'Status'].map((heading) => (
                  <th
                    key={heading}
                    scope="col"
                    className="border-b border-line bg-surface-2 px-3 py-2 text-left text-caption font-bold tracking-wide whitespace-nowrap text-ink-subtle uppercase"
                  >
                    {heading}
                  </th>
                ))}
                <th scope="col" className="border-b border-line bg-surface-2 py-2 pr-2 pl-3 text-right">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {types.map((type) => (
                <tr key={type.id}>
                  <th scope="row" className="border-b border-line px-3 py-2 text-left text-small font-bold">
                    {type.name}
                  </th>
                  <td className="border-b border-line px-3 py-2 text-small whitespace-nowrap text-ink-muted [font-variant-numeric:tabular-nums]">{type.requests}</td>
                  <td className="border-b border-line px-3 py-2 text-small whitespace-nowrap">
                    <span
                      className={`rounded-full px-2 py-0.5 text-caption font-bold ${
                        type.active ? 'bg-success/10 text-success' : 'bg-surface-2 text-ink-muted'
                      }`}
                    >
                      {type.active ? 'Active' : 'Retired'}
                    </span>
                  </td>
                  <td className="border-b border-line py-2 pr-2 pl-3 text-small">
                    {/* Each button has its own fixed slot, so the buttons line up in every row. */}
                    <div className="flex justify-end gap-2 whitespace-nowrap">
                      <div className="w-14">
                        <button type="button" aria-label={`Edit ${type.name}`} onClick={() => openPanel({ kind: 'edit', id: type.id })} className={`${ACTION} w-full`}>
                          Edit
                        </button>
                      </div>
                      <div className="w-24">
                        {type.active ? (
                          <button type="button" aria-label={`Retire ${type.name}`} onClick={() => openPanel({ kind: 'retire', id: type.id })} className={`${ACTION} w-full`}>
                            Retire
                          </button>
                        ) : (
                          <button type="button" aria-label={`Restore ${type.name}`} disabled={saving} onClick={() => void restore(type)} className={`${ACTION} w-full disabled:opacity-40`}>
                            Restore
                          </button>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  )
}
